import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, viewChild } from '@angular/core';
import { SlotsCardSelectionComponent } from '@components/slots-card-selection/slots-card-selection.component';
import { BOOKING_PAGE_VIEWS, CALENSO_SCROLL_EVENT, GTM_EVENTS } from '@constants/app.constants';
import { DATE_ISO_FORMAT, DATE_TIME_ISO_FORMAT, DATE_TIME_ISO_T_FORMAT } from '@constants/date.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { environment } from '@environments/environment';
import { AppointmentsSlotsDao } from '@models/availability.model';
import { StoreDbModel } from '@models/store-db.model';
import { TimezoneDbModel } from '@models/timezone.model';
import { WidgetAppointmentGroupModel } from '@models/widget-appointment-group.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressFormatPipe } from '@pipes/address-format.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { TrustHtmlPipe } from '@pipes/trust-html.pipe';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { TimezoneService } from '@services/feature/timezone.service';
import { WidgetService } from '@services/feature/widget.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { HostPageScrollService } from '@services/utils/host-page-scroll.service';
import { workerServesStore } from '@services/utils/store-scope.util';
import { resolveSlotWorkerUuids, servicesRequestResources } from '@services/utils/slot-workers.util';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { CalioCalendarPickerComponent } from '@ui-lib/calio-calendar/calio-calendar-picker/calio-calendar-picker.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import { WeekDay } from 'calendar-utils';
import moment from 'moment';

const pipes = [CalioTranslationPipe, CalioMeetingTemplatePipe, TrustHtmlPipe, AddressFormatPipe, DatePipe];
const components = [CswTextComponent, CalioCalendarPickerComponent, SlotsCardSelectionComponent, ButtonComponent];
const modules = [TranslateModule];

@Component({
  selector: 'app-slots-selection',
  templateUrl: './slots-selection.component.html',
  styleUrls: ['./slots-selection.component.scss'],
  imports: [...components, ...modules, ...pipes]
})
export class SlotsSelectionComponent implements OnInit, OnDestroy {

  private customEventService = inject(CustomEventService);
  private dateUtilService = inject(DateUtilService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private translate = inject(TranslateService);
  public bookingService = inject(BookingService);
  public widgetService = inject(WidgetService);
  public helperService = inject(HelperService);
  private timezoneService = inject(TimezoneService);
  private hostPageScrollService = inject(HostPageScrollService);
  private slotsCardSection = viewChild<ElementRef<HTMLElement>>('slotsCardSection');

  protected readonly dateIsoFormat = DATE_ISO_FORMAT;
  protected readonly dateTimeIsoFormat = DATE_TIME_ISO_FORMAT;

  get timeFormat(): string {
    return this.dateUtilService.userTimeFormat;
  }
  protected readonly workerAvatarBaseUrl = environment.workerAvatarBaseUrl;
  protected readonly baseUrl = environment.imageHostName;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  protected widgetGroups: WidgetAppointmentGroupModel[] = [];
  protected availableDays: string[] = [];
  protected showSlotGhostElement = false;
  protected freeAppointments: {
    dayPeriod: string,
    appointments: AppointmentsSlotsDao[],
    showDayPeriodLabel?: boolean,
    widgetGroup: WidgetAppointmentGroupModel
  }[] = [];
  protected freeAppointmentsCount = 0;
  protected noAppointments = false;
  protected selectedAppointment: AppointmentsSlotsDao;
  protected selectedDate: Date;
  protected slotGhostElements = new Array<number>(8);
  protected lang: string;
  protected loadCalendarPicker = false;
  protected isGTMEventSent: boolean;
  protected availableSlotsArray: AppointmentsSlotsDao[] = [];
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  protected showSlotsSelectionRequiredError = false;
  protected selectedWorker = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker;

  // Display-only: prefer the store the customer selected over the worker's primary store
  // (they can differ for a multi-store worker), falling back to the worker's own store when
  // no real store was selected — identical to before for single-store / no-selection paths.
  protected get previewStore(): StoreDbModel {
    // Guarded like displayStore() in workers-selection: only prefer the selected store when the
    // chosen worker actually serves it, so the preview cannot show a store the booking won't use.
    const selected = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore;
    return selected?.id && workerServesStore(this.selectedWorker, selected.id)
      ? selected
      : this.selectedWorker?.store;
  }

  @Input() protected timezones = new Array<TimezoneDbModel>();

  @Output('onTimezoneChange') onTimezoneChangeEvent = new EventEmitter<string>();
  @Output() nextPageEvent = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();

  constructor() {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.isGTMEventSent = false;
    this.loadSlotsSelection();
  }

  onTimezoneChange(event: string): void {
    this.onTimezoneChangeEvent.emit(event);
    this.bookingService.getDateByPlaceholder();
    this.loadSlotsSelection();
  }

  loadSlotsSelection(): void {
    this.bookingService.loadAppointmentCartItemsFromLocalStorage();
    this.loadCalendarPicker = true;
    this.getWidgetAppointmentGroups();
    if (this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedDate) {
      this.selectedDate = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedDate;
    }
    this.initAppointments();
  }

  initAppointments(): void {
    const timezone = this.bookingService.selectedBookingData?.timezone || this.timezoneService.localTimezone;
    let newDate = new Date(moment.tz(moment(), timezone).format(DATE_TIME_ISO_T_FORMAT));
    if (this.bookingService.isMeetingTypeStepInFlow() &&
      this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId !== 1
    ) {
      if (
        this.bookingService.selectedBookingData &&
        (
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId ||
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId === 0
        ) &&
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds &&
        (
          this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedWorker?.id ||
          this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedWorker?.id === 0
        )
      ) {
        if (this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedWorker?.id === 0) {
          // Initially, the storeId was always 0 for anyone feature (Sanitas).
          // Later, support for `selectedStoreId` was added for store selection (Helsana), because they always preselect a store.
          this.getAllExceptionDays(
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId,
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId || 0,
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.join(),
            this.selectedDate ? this.selectedDate : newDate,
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId
          );
        } else {
          this.getAllExceptionDays(
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId,
            // The advisor row is store-scoped now (POST workers/filter contains no Stores), so
            // `.store` is absent and this argument would fall to undefined - which the API reads
            // as store_id 0, the "any store" semantics. Fall back to the store that was answered.
            this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedWorker?.store?.id
              ?? this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedStoreId,
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.join(),
            this.selectedDate ? this.selectedDate : newDate,
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId
          );
        }
      } else {
        // TODO handling false case
        LoggerService.warn('SMART_WIDGET_WARNING: Worker, service and worker date is mandatory');
      }
    } else {
      if (
        this.bookingService.selectedBookingData &&
        (
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId
          || this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId === 0
        ) &&
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds &&
        (
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId ||
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId === 0
        )
      ) {
        this.getAllExceptionDays(
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.join(),
          this.selectedDate ? this.selectedDate : newDate
        );
      } else {
        // TODO handling false case
        LoggerService.warn('SMART_WIDGET_WARNING: Worker, service and store selection is mandatory');
      }
    }
  }

  onSelectedDays(data: {
    day: WeekDay[],
    isUserInteracted: boolean
  }): void {
    // Reset Error
    this.showSlotsSelectionRequiredError = false;
    this.noAppointments = false;
    this.freeAppointments = [];
    const event = data.day;
    this.selectedDate = event[0].date;
    this.selectedDate.setHours(0, 0, 0, 0);
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedDate = this.selectedDate;
    this.getFreeAppointments(this.dateUtilService.getFormattedDateByFormat(event[0].date, this.dateIsoFormat));

    this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);

    if (data.isUserInteracted) {
      this.googleAnalyticsService.triggerApoointmentStartEvent();

      if (!this.isGTMEventSent) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_available_appointments_start);
        this.isGTMEventSent = true;
      }

      if (this.widgetService.isWebComponentMode) {
        this.hostPageScrollService.requestReveal(() => this.slotsCardSection()?.nativeElement ?? null);
      }
    }
  }

  onMonthChangedEvent(event: Date): void {
    if (
      this.bookingService.isMeetingTypeStepInFlow() &&
      this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId !== 1
    ) {
      if (this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId === 0) {
        this.getAllExceptionDays(
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId || 0,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.join(),
          event,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId
        );
      } else {
        this.getAllExceptionDays(
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId,
          // The advisor row is store-scoped now (POST workers/filter contains no Stores), so
          // `.store` is absent and this argument would fall to undefined - which the API reads
          // as store_id 0, the "any store" semantics. Fall back to the store that was answered.
          this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedWorker?.store?.id
            ?? this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedStoreId,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.join(),
          event,
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId
        );
      }
    } else {
      this.getAllExceptionDays(
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId,
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId,
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.join(),
        event
      );
    }
  }

  previous(): void {
    this.isGTMEventSent && this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_available_appointments_end);

    // If combi service is selected, skip worker selection and go back to services view
    if (this.bookingService.isCombiServiceSelected) {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SERVICES_VIEW, isUserInteracted: true });
    } else {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.WORKERS_VIEW, isUserInteracted: true });
    }
  }

  next(): void {
    // Slots are not selected and clicked on next then show error.
    if (!(this.bookingService?.selectedBookingData?.cartItems?.length > 0)) {
      this.showSlotsSelectionRequiredError = true;
      return;
    }

    this.googleAnalyticsService.triggerApoointmentStartEvent();

    if (!this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_available_appointments_start);
      this.isGTMEventSent = true;
    }

    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_available_appointments_end);
    if (this.bookingService.isAutoLoadInPersonalForm()) {
      // [auto_load_booking_questions_placement=personal_form] The auto-loaded questions render
      // INSIDE the personal-details form (personal-form-view.component.ts), never in the dedicated
      // after-slots step — route straight there regardless of resolve/pending/failed state. A
      // failed or slow fetch must never block the contact form (item 8): personal-form-view waits
      // on whenAutoLoadSettled() itself before building customerFormFields, and degrades
      // gracefully (contact fields still render) on failure.
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.PERSONAL_FORM_VIEW, isUserInteracted: true });
    } else {
      // [auto_load_booking_questions review-fix #3/#5] A resolve fired at SERVICES_VIEW may still
      // be in flight, or may already have errored, by the time the user reaches slots-next — in
      // both cases the id list alone cannot be trusted: route into the questions step anyway so it
      // can either wait for the pending fetch (loader, #6) or surface the misconfiguration error
      // (#3), instead of silently skipping straight to the personal form and dropping a
      // required question. Flag OFF -> both extra checks are always false.
      // Guards on getPlacementTargetQuestionIds(): before-mode ON already asked the
      // context questions up front, so only service-resolved questions justify the AFTER step —
      // before-mode ON + auto-load OFF yields an empty target and falls through to the personal
      // form (the original before-mode routing, byte-for-byte); before-mode OFF is identical
      // to the previous getEffectiveBookingQuestionIds() guard (AC3/AC5).
      const autoLoadEnabled = this.bookingService.isAutoLoadBookingQuestionsEnabled();
      const autoLoadPendingOrFailed = autoLoadEnabled
        && (this.bookingService.isAutoLoadQuestionsResolving() || this.bookingService.hasAutoLoadFetchFailed());

      if (this.bookingService.getPlacementTargetQuestionIds().length > 0 || autoLoadPendingOrFailed) {
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_QUESTIONS_VIEW_AFTER, isUserInteracted: true });
      } else {
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.PERSONAL_FORM_VIEW, isUserInteracted: true });
      }
    }
  }

  private getAllExceptionDays(workerId: number, storeId: number, appointmentIds: string, date: Date, meetingTypeId?: number): void {
    let startDate: string;
    const timezone = this.bookingService.selectedBookingData?.timezone || this.timezoneService.localTimezone;
    let newDate = new Date(moment.tz(moment(), timezone).format(DATE_TIME_ISO_T_FORMAT));
    if (this.dateUtilService.isSame(date, newDate, 'month')) {
      startDate = this.dateUtilService.getFormattedDateByFormat(newDate, this.dateIsoFormat);
    } else {
      startDate = this.dateUtilService.getFormattedDateByFormat(this.dateUtilService.getStartOf(date, 'month'), this.dateIsoFormat);
    }

    this.bookingService.getDaysWiseFreeSlots(
      workerId,
      storeId,
      appointmentIds,
      startDate,
      this.bookingService.partnerData?.is_timezone_enabled ? this.bookingService.effectiveDisplayTimezone : undefined,
      meetingTypeId
    ).subscribe({
      next: availableSlots => {
        this.availableSlotsArray = [];
        if (availableSlots && !('errors' in availableSlots)) {
          const hasResourcesRequested = servicesRequestResources(
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices
          );
          // preparing array that has day wise availabilities
          availableSlots.available_minutes_blocks?.forEach(daysWiseSlots => {
            daysWiseSlots.workers_minutes_blocks?.forEach(workerMinutesBlock => {
              workerMinutesBlock.minutes_blocks.forEach(availabilities => {
                availabilities.appointments_slots?.forEach(slots => {
                  const selectedWorkerId = Number(this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId);
                  const workerUuids = resolveSlotWorkerUuids(workerMinutesBlock, !(selectedWorkerId > 0), hasResourcesRequested);

                  !this.availableSlotsArray[daysWiseSlots.date] && (this.availableSlotsArray[daysWiseSlots.date] = []);
                  this.availableSlotsArray[daysWiseSlots.date].push({
                    start: slots.appointment_start,
                    end: slots.appointment_end,
                    worker: workerMinutesBlock.worker,
                    workerUuids,
                    combiServiceWorkersMapping: slots.combiServiceWorkersMapping || workerMinutesBlock.combi_workers || {},
                    store: workerMinutesBlock.store,
                    startHour: parseInt(slots.appointment_start.split(' ')[1].split(':')[0], 10),
                    short_start: slots.appointment_short_start,
                  });
                });
              });
            });
          });
        }

        if (Object.keys(this.availableSlotsArray)?.length > 0) {
          this.availableDays = Object.keys(this.availableSlotsArray).map((key) => key);
        } else {
          this.availableDays = [];
        }

        if (!this.availableDays.includes(this.dateUtilService.getFormattedDateByFormat(this.selectedDate, this.dateIsoFormat))) {
          this.selectedDate = undefined
        } else if (
          !this.selectedDate &&
          this.availableDays.includes(this.dateUtilService.getFormattedDateByFormat(this.selectedDate, this.dateIsoFormat))) {
          this.selectedDate = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedDate;
        }

        this.customEventService.refreshCalendar.emit();
      },
      error: (error: HttpErrorResponse) => this.customEventService.refreshCalendar.emit()
    });
  }

  private getWidgetAppointmentGroups(): void {
    this.bookingService.getWidgetAppointmentGroups().subscribe({
      next: (widgetGroups: WidgetAppointmentGroupModel[]) => {
        !widgetGroups && (widgetGroups = []);
        widgetGroups.forEach(widgetGroup => widgetGroup.startHour = parseInt((widgetGroup.start as string).split('T')[1].split(':')[0], 10))
        this.widgetGroups = widgetGroups.sort((a, b) => a.startHour - b.startHour);
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  private getFreeAppointments(date: string): void {
    this.freeAppointmentsCount = 0;
    this.showSlotGhostElement = true;
    this.timeoutId = setTimeout(() => {
      // No parent frame in WC mode — this scroll event would post to the host page itself.
      if (Number(this.bookingService.partnerData?.scroll_to_element_feature_is_enabled) === 1
        && !this.widgetService.isWebComponentMode) {
        const slotsCardSection = document.getElementById('slotsCardSection');
        const parentOrigin = window.location.ancestorOrigins?.[0];
        window.parent.postMessage({ eventName: CALENSO_SCROLL_EVENT, data: { top: slotsCardSection.getBoundingClientRect().top } }, parentOrigin || '*');
      }
      this.showSlotGhostElement = false;
    }, 500);

    this.customEventService.resetSlotSettingsEvent.emit();

    let freeAppointments: AppointmentsSlotsDao[];
    freeAppointments = this.availableSlotsArray[date];
    this.freeAppointmentsCount = freeAppointments.length;
    this.freeAppointments = [];
    freeAppointments = freeAppointments.sort((a, b) => Number(a.short_start) - Number(b.short_start));

    let tempFreeAppointments: {
      dayPeriod: string,
      appointments: AppointmentsSlotsDao[],
      showDayPeriodLabel?: boolean,
      widgetGroup: WidgetAppointmentGroupModel
    }[] = [];

    this.widgetGroups?.forEach(widgetGroup => {
      tempFreeAppointments.push({
        dayPeriod: widgetGroup.name,
        appointments: [],
        showDayPeriodLabel: false,
        widgetGroup
      });
    });

    tempFreeAppointments.push({
      dayPeriod: undefined,
      appointments: [],
      showDayPeriodLabel: false,
      widgetGroup: undefined
    });

    if (this.widgetGroups?.length) {
      const notGroupedAppointments: AppointmentsSlotsDao[] = [];
      for (let i = 0; freeAppointments.length > i; i++) {
        for (const freeAppointmentGroup of tempFreeAppointments) {
          if (freeAppointmentGroup.widgetGroup) {
            const freeAppointmentStart = moment(moment().format(this.dateIsoFormat) + ' ' + moment(freeAppointments[i].start).format(this.timeFormat), this.dateTimeIsoFormat).toDate();
            const freeAppointmentEnd = moment(moment().format(this.dateIsoFormat) + ' ' + moment(freeAppointments[i].end).format(this.timeFormat), this.dateTimeIsoFormat).toDate();

            let widgetGroupStart = moment(freeAppointmentGroup.widgetGroup.start, this.dateTimeIsoFormat).toDate();
            let widgetGroupEnd = moment(freeAppointmentGroup.widgetGroup.end, this.dateTimeIsoFormat).toDate();
            widgetGroupStart = moment(moment().format(this.dateIsoFormat) + ' ' + moment(widgetGroupStart).format(this.timeFormat), this.dateTimeIsoFormat).toDate();
            widgetGroupEnd = moment(moment().format(this.dateIsoFormat) + ' ' + moment(widgetGroupEnd).format(this.timeFormat), this.dateTimeIsoFormat).toDate();

            if (moment(widgetGroupStart).isAfter(moment(widgetGroupEnd))) {
              widgetGroupEnd = moment(widgetGroupEnd).add(1, 'day').toDate();
              if (
                (
                  moment(freeAppointmentStart).isSameOrAfter(moment(widgetGroupStart)) ||
                  moment(freeAppointmentStart).add(1, 'day').isBetween(moment(widgetGroupStart), moment(widgetGroupEnd), undefined, '[]')
                ) &&
                moment(freeAppointmentEnd).isSameOrBefore(moment(widgetGroupEnd))
              ) {
                freeAppointments[i].isGrouped = true;
                freeAppointmentGroup.appointments.push(freeAppointments[i]);
                freeAppointmentGroup.showDayPeriodLabel = true;
                break;
              }
            } else {
              if (
                moment(freeAppointmentStart).isSameOrAfter(moment(widgetGroupStart)) &&
                moment(freeAppointmentStart).isBefore(moment(widgetGroupEnd))
              ) {
                freeAppointments[i].isGrouped = true;
                freeAppointmentGroup.appointments.push(freeAppointments[i]);
                freeAppointmentGroup.showDayPeriodLabel = true;
                break;
              }
            }

          }
        }
        (!freeAppointments[i].isGrouped) && notGroupedAppointments.push(freeAppointments[i]);
      }
      if (notGroupedAppointments.length > 0) {
        tempFreeAppointments[tempFreeAppointments.length - 1].showDayPeriodLabel = true;
        tempFreeAppointments[tempFreeAppointments.length - 1].appointments = Object.assign([], notGroupedAppointments);
      }
    } else {
      for (let i = 0; freeAppointments.length > i; i++) {
        tempFreeAppointments[tempFreeAppointments.length - 1].showDayPeriodLabel = true;
        tempFreeAppointments[tempFreeAppointments.length - 1].appointments.push(freeAppointments[i]);
      }
    }

    let appointmentOrderIndex = 0;
    tempFreeAppointments.forEach(tempFreeAppointment => {
      tempFreeAppointment.appointments = tempFreeAppointment.appointments ? tempFreeAppointment.appointments : [];
      (appointmentOrderIndex > 9) && (tempFreeAppointment.showDayPeriodLabel = false);
      tempFreeAppointment.appointments?.forEach(appointment => appointment.appointmentOrderIndex = appointmentOrderIndex++);

      // Randomize worker order for same time slots if:
      // 1. There are appointments available
      // 2. Worker randomization setting is enabled
      // 3. Duplicate minutes slots removal is disabled
      if (
        tempFreeAppointment.appointments.length > 0 &&
        this.bookingService.partnerData?.enable_workers_randomize_with_same_time_slots === 1 &&
        this.bookingService.partnerData?.widget_remove_duplicate_minutes_slots === 0
      ) {
        tempFreeAppointment.appointments = this.randomizeWorkersWithSameTimeSlots(tempFreeAppointment.appointments);
      }
    })

    this.freeAppointments = tempFreeAppointments.filter(appointmentGroup => appointmentGroup?.appointments?.length > 0);

    (!freeAppointments || freeAppointments.length === 0) && (this.noAppointments = true);
  }

  ngOnDestroy(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }
  }

  /**
   * Randomizes workers with the same start and end times while maintaining chronological order
   * @param appointments Array of appointments to process
   * @returns Array with randomized workers for same time slots
   */
  private randomizeWorkersWithSameTimeSlots(appointments: AppointmentsSlotsDao[]): AppointmentsSlotsDao[] {
    const groups = new Map<string, AppointmentsSlotsDao[]>();

    // Group appointments by start and end times
    appointments.forEach(appointment => {
      const key = `${appointment.short_start}-${appointment.end}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(appointment);
    });

    // Randomize workers within each group and flatten the result
    const result: AppointmentsSlotsDao[] = [];
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
      const aStart = a.split('-')[0];
      const bStart = b.split('-')[0];
      return Number(aStart) - Number(bStart);
    });

    sortedKeys.forEach(key => {
      const group = groups.get(key)!;
      // Shuffle the group to randomize workers with same start/end times
      const shuffledGroup = this.helperService.shuffleArray([...group]);
      result.push(...shuffledGroup);
    });

    return result;
  }
}
